import React, { useState } from "react";
import { ContentItem } from "@/types/content";
import { ContentIcon } from "./ContentIcon";
import { ContentActionsMenu } from "./ContentActionsMenu";
import { Room } from "@/types/rooms";
import { formatDistanceToNow } from "date-fns";
import { Link } from "react-router-dom";
import { useTranslation } from "@/context/LanguageContext";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type SortField = "title" | "createdAt" | "visibility";
type SortOrder = "asc" | "desc";

export interface ContentListProps {
  items: ContentItem[];
  buildHref?: (item: ContentItem) => string;
  rooms?: Room[];
  onAddToRoom?: (item: ContentItem, roomId: string) => void;
  onShare?: (item: ContentItem) => void;
  onDelete?: (item: ContentItem) => void;
  onCardClick?: (item: ContentItem) => void;
}

export const ContentList: React.FC<ContentListProps> = ({
  items,
  buildHref,
  rooms,
  onAddToRoom,
  onShare,
  onDelete,
  onCardClick,
}) => {
  const { t, formatNumber } = useTranslation();
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

  const href = (i: ContentItem) => {
    if (buildHref) return buildHref(i);
    return i.type === "chat" ? `/chat/${i.id}` : `/content/${i.id}`;
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const sortedItems = [...items].sort((a, b) => {
    let comparison = 0;

    if (sortField === "title") {
      comparison = a.title.localeCompare(b.title);
    } else if (sortField === "createdAt") {
      comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    } else if (sortField === "visibility") {
      const aVis = a.visibility || "private";
      const bVis = b.visibility || "private";
      comparison = aVis.localeCompare(bVis);
    }

    return sortOrder === "asc" ? comparison : -comparison;
  });

  const formatRelativeTime = (date: string) => {
    return formatDistanceToNow(new Date(date), { addSuffix: true });
  };

  return (
    <div className="w-full">
      <Table>
        <TableHeader>
          <TableRow className="border-b border-border/60 hover:bg-transparent">
            <TableHead 
              className="w-auto cursor-pointer text-muted-foreground hover:text-foreground"
              onClick={() => handleSort("title")}
            >
              <div className="flex items-center gap-1">
                {t("room.title")}
                {sortField === "title" && (
                  <svg
                    className={`h-4 w-4 transition-transform ${
                      sortOrder === "desc" ? "rotate-180" : ""
                    }`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="m18 15-6-6-6 6" />
                  </svg>
                )}
              </div>
            </TableHead>
            <TableHead 
              className="hidden md:table-cell w-[140px] cursor-pointer text-muted-foreground hover:text-foreground"
              onClick={() => handleSort("createdAt")}
            >
              <div className="flex items-center gap-1">
                {t("room.addedOn")}
                {sortField === "createdAt" && (
                  <svg
                    className={`h-4 w-4 transition-transform ${
                      sortOrder === "desc" ? "rotate-180" : ""
                    }`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="m18 15-6-6-6 6" />
                  </svg>
                )}
              </div>
            </TableHead>
            <TableHead 
              className="hidden md:table-cell w-[120px] cursor-pointer text-muted-foreground hover:text-foreground"
              onClick={() => handleSort("visibility")}
            >
              <div className="flex items-center gap-1">
                {t("room.visibility")}
                {sortField === "visibility" && (
                  <svg
                    className={`h-4 w-4 transition-transform ${
                      sortOrder === "desc" ? "rotate-180" : ""
                    }`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="m18 15-6-6-6 6" />
                  </svg>
                )}
              </div>
            </TableHead>
            <TableHead className="w-[100px] text-right text-muted-foreground">
              {formatNumber(items.length)} {t("room.items")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedItems.map((item) => (
            <TableRow
              key={item.id}
              className="border-b border-border/60 transition-colors hover:bg-muted/50"
            >
              <TableCell className="font-medium">
                {onCardClick ? (
                  <button
                    onClick={() => onCardClick(item)}
                    className="flex items-center gap-2 text-foreground hover:text-primary text-left w-full"
                  >
                    <ContentIcon type={item.type} />
                    <span className="truncate">{item.title}</span>
                  </button>
                ) : (
                  <Link
                    to={href(item)}
                    className="flex items-center gap-2 text-foreground hover:text-primary"
                  >
                    <ContentIcon type={item.type} />
                    <span className="truncate">{item.title}</span>
                  </Link>
                )}
              </TableCell>
              <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                {formatRelativeTime(item.createdAt)}
              </TableCell>
              <TableCell className="hidden md:table-cell text-sm">
                <span className="capitalize text-muted-foreground">
                  {item.visibility === "public" ? t("common.public") : t("common.private")}
                </span>
              </TableCell>
              <TableCell className="text-right">
                <ContentActionsMenu
                  item={item}
                  rooms={rooms}
                  onAddToRoom={onAddToRoom}
                  onShare={onShare}
                  onDelete={onDelete}
                  variant="list"
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
